import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import { runsApi, RecentRunItem, AgentSummary, OutcomePrediction } from '../utils/api'
import { formatTimeAgo, formatProbability, getAgentIcon } from '../utils/format'
import clsx from 'clsx'

function RunStatusBadge({ status }: { status: RecentRunItem['status'] }) {
  return (
    <span
      className={clsx(
        'px-2 py-0.5 rounded-full text-xs font-mono',
        status === 'running' && 'bg-signal-blue/20 text-signal-blue',
        status === 'completed' && 'bg-signal-green/20 text-signal-green',
        status === 'pending' && 'bg-star-dim/20 text-star-dim'
      )}
    >
      {status === 'running' && (
        <span className="inline-block w-2 h-2 bg-signal-blue rounded-full mr-1.5 animate-pulse" />
      )}
      {status}
    </span>
  )
}

function DecisionBadge({ decision }: { decision: 'buy' | 'sell' | 'hold' | null }) {
  if (!decision) return null
  return (
    <span
      className={clsx(
        'px-2 py-0.5 rounded text-xs font-mono font-semibold',
        decision === 'buy' && 'bg-signal-green/20 text-signal-green',
        decision === 'sell' && 'bg-signal-red/20 text-signal-red',
        decision === 'hold' && 'bg-star-dim/20 text-star-dim'
      )}
    >
      {decision.toUpperCase()}
    </span>
  )
}

function AgentPill({ agent }: { agent: AgentSummary }) {
  const icon = getAgentIcon(agent.name)
  return (
    <div
      className={clsx(
        'flex items-center gap-1.5 px-2 py-1 rounded-full text-xs transition-all',
        agent.status === 'complete' && 'bg-signal-green/10 text-signal-green',
        agent.status === 'running' && 'bg-signal-blue/10 text-signal-blue',
        agent.status === 'pending' && 'bg-void-100 text-star-dim'
      )}
    >
      {agent.status === 'running' && (
        <span className="w-1.5 h-1.5 bg-signal-blue rounded-full animate-pulse" />
      )}
      <span>{icon}</span>
      <span className="capitalize">{agent.name}</span>
      {agent.status === 'complete' && agent.evidence_count > 0 && (
        <span className="text-[10px] opacity-70">({agent.evidence_count})</span>
      )}
    </div>
  )
}

function AgentDetails({ agent }: { agent: AgentSummary }) {
  if (agent.status !== 'complete') return null

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="border-t border-star-dim/10 pt-2 mt-2"
    >
      <div className="flex items-start gap-2">
        <span className="text-lg">{getAgentIcon(agent.name)}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-xs text-star capitalize">{agent.name}</span>
            {/* Only show probability for synthesis - all others return directional data */}
            {agent.name === 'synthesis' && agent.prob !== null && (
              <span className="text-xs font-mono text-signal-blue">
                {formatProbability(agent.prob)}
              </span>
            )}
            {agent.name === 'synthesis' && agent.confidence !== null && agent.confidence > 0 && (
              <span className="text-xs text-star-dim">
                ({(agent.confidence * 100).toFixed(0)}% conf)
              </span>
            )}
            {agent.evidence_count > 0 && (
              <span className="text-xs text-star-dim">
                {agent.evidence_count} source{agent.evidence_count !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          {agent.summary && (
            <p className="text-xs text-star-muted leading-relaxed">{agent.summary}</p>
          )}
        </div>
      </div>
    </motion.div>
  )
}

function OutcomeProbabilitiesTable({ outcomes }: { outcomes: OutcomePrediction[] }) {
  // Sort by absolute edge (best opportunities first)
  const sortedOutcomes = [...outcomes].sort((a, b) =>
    Math.abs(b.edge || 0) - Math.abs(a.edge || 0)
  )

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="border-t border-star-dim/10 pt-3 mt-3"
    >
      <div className="text-xs text-star-dim font-mono mb-2">All Outcome Predictions</div>
      <div className="space-y-1">
        {sortedOutcomes.map((outcome) => (
          <div
            key={outcome.qid}
            className={clsx(
              'flex items-center justify-between px-2 py-1.5 rounded text-xs',
              outcome.edge && Math.abs(outcome.edge) > 0.1
                ? 'bg-signal-blue/5 border border-signal-blue/20'
                : 'bg-void-100'
            )}
          >
            <span className="text-star truncate flex-1 mr-2" title={outcome.name}>
              {outcome.name}
            </span>
            <div className="flex items-center gap-3 shrink-0">
              <span className="font-mono text-star">
                {formatProbability(outcome.prob)}
              </span>
              <span className="font-mono text-star-dim text-[10px]">
                mkt: {outcome.market_prob !== undefined ? formatProbability(outcome.market_prob) : '—'}
              </span>
              <span
                className={clsx(
                  'font-mono w-14 text-right',
                  outcome.edge && outcome.edge > 0.02 && 'text-signal-green',
                  outcome.edge && outcome.edge < -0.02 && 'text-signal-red',
                  (!outcome.edge || Math.abs(outcome.edge) <= 0.02) && 'text-star-dim'
                )}
              >
                {outcome.edge !== null
                  ? `${outcome.edge > 0 ? '+' : ''}${(outcome.edge * 100).toFixed(1)}%`
                  : '—'}
              </span>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  )
}

function RunCard({ run, expanded, onToggle }: { run: RecentRunItem; expanded: boolean; onToggle: () => void }) {
  const isRunning = run.status === 'running' || run.status === 'pending'
  const hasAgentData = run.agents && run.agents.length > 0
  const completedAgents = run.agents?.filter(a => a.status === 'complete') || []

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={clsx(
        'glass-card p-4 relative overflow-hidden cursor-pointer',
        isRunning && 'border-signal-blue/30',
        expanded && 'col-span-full'
      )}
      onClick={onToggle}
    >
      {/* Scan line for running */}
      {isRunning && <div className="scan-line" />}

      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          {/* Show specific outcome if available, otherwise show parent question */}
          <Link
            to={`/question/${run.question_id}`}
            className="text-star hover:text-signal-blue transition-colors line-clamp-2 text-sm font-medium"
            onClick={(e) => e.stopPropagation()}
          >
            {run.analyzed_outcome_title || run.question_title}
          </Link>
          {/* Show parent question as subtitle if we're showing a specific outcome */}
          {run.analyzed_outcome_title && (
            <div className="text-xs text-star-dim mt-0.5 truncate">
              {run.question_title}
            </div>
          )}
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-xs text-star-dim font-mono">#{run.run_id}</span>
            <span className="text-star-dim">·</span>
            <span className="text-xs text-star-dim">
              {run.started_at ? formatTimeAgo(run.started_at) : 'Starting...'}
            </span>
            {run.orchestrator_version && (
              <>
                <span className="text-star-dim">·</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-signal-purple/10 text-signal-purple border border-signal-purple/30 font-mono">
                  {run.orchestrator_version}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {run.decision && <DecisionBadge decision={run.decision} />}
          <RunStatusBadge status={run.status} />
        </div>
      </div>

      {/* Agent progress pills */}
      {hasAgentData && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {run.agents.map((agent) => (
            <AgentPill key={agent.name} agent={agent} />
          ))}
        </div>
      )}

      {/* Results for completed */}
      {run.status === 'completed' && run.post_prob !== null && (() => {
        // Find best edge outcome from multi-outcome predictions
        const bestEdgeOutcome = run.outcome_probabilities && run.outcome_probabilities.length > 0
          ? run.outcome_probabilities.reduce((best, curr) =>
              Math.abs(curr.edge || 0) > Math.abs(best.edge || 0) ? curr : best
            )
          : null;

        // Use best edge outcome values if available, otherwise fall back to primary
        const displayProb = bestEdgeOutcome?.prob ?? run.post_prob;
        const displayMarket = bestEdgeOutcome?.market_prob ?? run.market_price;
        const displayEdge = bestEdgeOutcome?.edge ?? run.edge;
        const hasMultiOutcome = run.outcome_probabilities && run.outcome_probabilities.length > 1;

        return (
          <div className="mb-3">
            {/* Best edge outcome indicator */}
            {hasMultiOutcome && bestEdgeOutcome && (
              <div className="flex items-center gap-2 mb-2 text-xs">
                <span className="text-signal-blue font-medium">Best Edge:</span>
                <span className="text-star truncate">{bestEdgeOutcome.name}</span>
                <span className="text-star-dim">
                  ({run.outcome_probabilities!.length} outcomes analyzed)
                </span>
              </div>
            )}

            <div className="grid grid-cols-4 gap-3">
              <div>
                <span className="text-xs text-star-dim">Prediction</span>
                <div className="text-lg font-mono text-star">
                  {formatProbability(displayProb)}
                </div>
              </div>
              <div>
                <span className="text-xs text-star-dim">Market</span>
                <div className="text-lg font-mono text-star-muted">
                  {displayMarket !== null && displayMarket !== undefined ? formatProbability(displayMarket) : '—'}
                </div>
              </div>
              <div>
                <span className="text-xs text-star-dim">Edge</span>
                <div
                  className={clsx(
                    'text-lg font-mono',
                    displayEdge !== null && displayEdge !== undefined && displayEdge > 0 && 'text-signal-green',
                    displayEdge !== null && displayEdge !== undefined && displayEdge < 0 && 'text-signal-red',
                    (displayEdge === null || displayEdge === undefined) && 'text-star-muted'
                  )}
                >
                  {displayEdge !== null && displayEdge !== undefined
                    ? `${displayEdge > 0 ? '+' : ''}${(displayEdge * 100).toFixed(1)}%`
                    : '—'}
                </div>
              </div>
              <div>
                <span className="text-xs text-star-dim">Confidence</span>
                <div className="text-lg font-mono text-star-muted">
                  {run.confidence !== null && run.confidence > 0 ? `${(run.confidence * 100).toFixed(0)}%` : '—'}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Expanded: Multi-outcome predictions (shown first if available) */}
      <AnimatePresence>
        {expanded && run.outcome_probabilities && run.outcome_probabilities.length > 0 && (
          <OutcomeProbabilitiesTable outcomes={run.outcome_probabilities} />
        )}
      </AnimatePresence>

      {/* Expanded agent details */}
      <AnimatePresence>
        {expanded && completedAgents.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-2"
          >
            <div className="text-xs text-star-dim font-mono mb-2">Agent Reasoning</div>
            {completedAgents.map((agent) => (
              <AgentDetails key={agent.name} agent={agent} />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Expand hint and details link */}
      <div className="flex items-center justify-between mt-2">
        {!expanded && (completedAgents.some(a => a.summary) || (run.outcome_probabilities && run.outcome_probabilities.length > 0)) && (
          <div className="text-xs text-star-dim opacity-50">
            {run.outcome_probabilities && run.outcome_probabilities.length > 0
              ? `Click to see ${run.outcome_probabilities.length} outcome predictions`
              : 'Click to expand'}
          </div>
        )}
        {expanded && <div />}
        <Link
          to={`/runs/${run.run_id}`}
          className="text-xs text-signal-blue hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          View full details →
        </Link>
      </div>
    </motion.div>
  )
}

export function LiveRuns() {
  const [expandedRun, setExpandedRun] = useState<number | null>(null)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['runs', 'recent'],
    queryFn: () => runsApi.recent(50, true),
    refetchInterval: 5000, // Poll every 5 seconds for live updates
  })

  const runs = data?.runs || []
  const runningRuns = runs.filter((r) => r.status === 'running' || r.status === 'pending')
  const completedRuns = runs.filter((r) => r.status === 'completed')

  // Calculate stats
  const avgEdge = completedRuns.length > 0
    ? completedRuns.reduce((sum, r) => sum + (r.edge || 0), 0) / completedRuns.length
    : 0
  const buyCount = completedRuns.filter(r => r.decision === 'buy').length
  const sellCount = completedRuns.filter(r => r.decision === 'sell').length

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
      {/* Page header */}
      <div className="mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-display font-semibold text-star mb-1 sm:mb-2">
          Live Runs
        </h2>
        <p className="text-star-muted text-xs sm:text-sm">
          Track prediction runs in real-time
        </p>
      </div>

      {/* Summary stats */}
      <div className="glass-card p-4 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4 sm:gap-6">
            <div className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 bg-signal-blue rounded-full animate-pulse" />
              <span className="text-star font-mono">{runningRuns.length}</span>
              <span className="text-star-muted text-sm">running</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 bg-signal-green rounded-full" />
              <span className="text-star font-mono">{completedRuns.length}</span>
              <span className="text-star-muted text-sm">completed</span>
            </div>
            {completedRuns.length > 0 && (
              <>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <span className="text-star-dim text-xs sm:text-sm">Edge:</span>
                  <span className={clsx(
                    'font-mono text-xs sm:text-sm',
                    avgEdge > 0 ? 'text-signal-green' : avgEdge < 0 ? 'text-signal-red' : 'text-star-muted'
                  )}>
                    {avgEdge > 0 ? '+' : ''}{(avgEdge * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="flex items-center gap-1 sm:gap-2">
                  <span className="text-signal-green font-mono text-xs sm:text-sm">{buyCount}B</span>
                  <span className="text-star-dim">/</span>
                  <span className="text-signal-red font-mono text-xs sm:text-sm">{sellCount}S</span>
                </div>
              </>
            )}
          </div>
          <button className="btn-secondary text-sm" onClick={() => refetch()}>
            Refresh
          </button>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 rounded-full border-2 border-signal-blue/30 border-t-signal-blue animate-spin" />
            <p className="text-star-muted font-mono text-sm">Loading runs...</p>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="glass-card p-8 text-center">
          <p className="text-signal-red font-mono mb-4">Failed to load runs</p>
          <button className="btn-primary" onClick={() => refetch()}>
            Try Again
          </button>
        </div>
      )}

      {/* Running runs section */}
      {runningRuns.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-display font-semibold text-star mb-4 flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-signal-blue rounded-full animate-pulse" />
            In Progress
          </h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence mode="popLayout">
              {runningRuns.map((run) => (
                <RunCard
                  key={run.run_id}
                  run={run}
                  expanded={expandedRun === run.run_id}
                  onToggle={() => setExpandedRun(expandedRun === run.run_id ? null : run.run_id)}
                />
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* Completed runs section */}
      {completedRuns.length > 0 && (
        <div>
          <h3 className="text-lg font-display font-semibold text-star mb-4">
            Recently Completed
          </h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence mode="popLayout">
              {completedRuns.map((run) => (
                <RunCard
                  key={run.run_id}
                  run={run}
                  expanded={expandedRun === run.run_id}
                  onToggle={() => setExpandedRun(expandedRun === run.run_id ? null : run.run_id)}
                />
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && runs.length === 0 && (
        <div className="glass-card p-12 text-center">
          <p className="text-star-muted font-mono text-lg mb-2">No runs yet</p>
          <p className="text-star-dim text-sm">
            Start a prediction from the{' '}
            <Link to="/questions" className="text-signal-blue hover:underline">
              Questions
            </Link>{' '}
            page
          </p>
        </div>
      )}
    </div>
  )
}
